import React, { useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';

import { styles } from '../WorkingHours.styles';
import { HolidayBlock } from '../Holidays.types';
import { HolidaysModalProps } from '../HolidaysModalContainer';
import {
  DAY_OF_WEEK_LABELS,
  daysBetweenInclusive,
  formatDateRange,
  formatMonthLabel,
  isoDate,
} from '../utils/holidayDateUtils';
import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';

const HolidaysModal = ({
  isOpen,
  sites,
  draft,
  calendarYear,
  calendarMonth,
  dragAnchor,
  dragHover,
  isLoading,
  isSaving,
  pendingDeleteId,
  isDeletingHoliday,
  savedHolidayIds,
  readOnly,
  onClose,
  onSave,
  onSetCalendarMonth,
  onStartDrag,
  onHoverDrag,
  onFinalizeDrag,
  onUpdateName,
  onUpdateRepeat,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: HolidaysModalProps) => {
  // Mirrors the mockup: a drag-select can end with the mouse anywhere on the page.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseUp = () => {
      if (dragAnchor) onFinalizeDrag();
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [isOpen, dragAnchor, onFinalizeDrag]);

  const todayKey = useMemo(() => {
    const now = new Date();
    return isoDate(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const savedIdSet = useMemo(() => new Set(savedHolidayIds), [savedHolidayIds]);

  if (!isOpen) return null;

  const site = sites.join(', ');
  const isMultiSite = sites.length > 1;
  const isSavedBlock = (block: HolidayBlock) => savedIdSet.has(block.id);
  const coversDay = (key: string) => (b: HolidayBlock) => b.start <= key && key <= b.end;
  // Unsaved first, so a day being added for some sites shows as pending even if saved for others.
  const blockAt = (key: string) =>
    draft.find(b => !isSavedBlock(b) && coversDay(key)(b)) ?? draft.find(coversDay(key));
  const blockTitle = (key: string) =>
    draft
      .filter(coversDay(key))
      .map(
        b =>
          `${b.name || 'Day off'}${isMultiSite ? ` · ${b.siteNames.join(', ')}` : ''}${
            isSavedBlock(b) ? '' : ' (unsaved)'
          }`
      )
      .join('\n');

  const previewRange = (() => {
    if (!dragAnchor || !dragHover) return null;
    return dragAnchor <= dragHover ? [dragAnchor, dragHover] : [dragHover, dragAnchor];
  })();

  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const firstWeekday = new Date(calendarYear, calendarMonth, 1).getDay();

  const goToMonth = (delta: number) => {
    let month = calendarMonth + delta;
    let year = calendarYear;
    if (month < 0) {
      month = 11;
      year -= 1;
    } else if (month > 11) {
      month = 0;
      year += 1;
    }
    onSetCalendarMonth({ year, month });
  };

  const handleClose = () => {
    KonnectAnalytics.productionHoursCloseHolidays({ site });
    onClose();
  };

  const handleSave = () => {
    KonnectAnalytics.productionHoursHolidaysSaved({ site });
    onSave();
  };

  const handleStartDrag = (dateKey: string) => {
    if (readOnly) return;
    KonnectAnalytics.productionHoursHolidaysDaySelected({ dateKey, site });
    onStartDrag({ dateKey });
  };

  const handleRequestDelete = (id: string) => {
    KonnectAnalytics.productionHoursHolidaysDeleteRequested({ id, site });
    onRequestDelete({ id });
  };

  const handleCancelDelete = () => {
    if (pendingDeleteId) {
      KonnectAnalytics.productionHoursHolidaysDeleteCancelled({ id: pendingDeleteId, site });
    }
    onCancelDelete();
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteId) {
      KonnectAnalytics.productionHoursHolidaysDeleteConfirmed({ id: pendingDeleteId, site });
    }
    onConfirmDelete();
  };

  const handleUpdateRepeat = (id: string, repeat: boolean) => {
    KonnectAnalytics.productionHoursHolidayBlockUpdated({ field: 'repeat', id, site });
    onUpdateRepeat({ id, repeat });
  };

  // Unsaved drafts float to the top — that's what the user is actively filling in
  // (name/repeat), so it shouldn't get buried under already-saved entries.
  const sortedDraft = [...draft].sort((a, b) => {
    const aUnsaved = !isSavedBlock(a);
    const bUnsaved = !isSavedBlock(b);
    if (aUnsaved !== bUnsaved) return aUnsaved ? -1 : 1;
    return a.start.localeCompare(b.start);
  });
  const pendingDeleteBlock = draft.find(b => b.id === pendingDeleteId) ?? null;
  const unsavedDraft = sortedDraft.filter(b => !isSavedBlock(b));

  const renderItem = (block: HolidayBlock) => {
    const single = block.start === block.end;
    const saved = isSavedBlock(block);
    return (
      <Box
        key={block.id}
        sx={{ ...styles.holidaysItem, ...(saved ? {} : styles.holidaysItemPending) }}>
        <Box sx={styles.holidaysItemTop}>
          <Box sx={styles.holidaysItemDateGroup}>
            <Box sx={styles.holidaysItemDate}>{formatDateRange(block.start, block.end)}</Box>
            {!single && (
              <Box sx={styles.holidaysItemSpan}>
                {daysBetweenInclusive(block.start, block.end)} days
              </Box>
            )}
            {!saved && <Box sx={styles.holidaysItemPendingBadge}>Unsaved</Box>}
          </Box>
          <IconButton
            size="small"
            disabled={readOnly}
            onClick={() => handleRequestDelete(block.id)}
            sx={styles.holidaysItemDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
        {isMultiSite && !saved && (
          <Box sx={styles.holidaysItemSites}>For: {block.siteNames.join(', ')}</Box>
        )}
        <TextField
          variant="standard"
          fullWidth
          placeholder="Name (optional)"
          value={block.name}
          disabled={readOnly}
          onChange={e => onUpdateName({ id: block.id, name: e.target.value })}
          onBlur={() =>
            KonnectAnalytics.productionHoursHolidayBlockUpdated({
              field: 'name',
              id: block.id,
              site,
            })
          }
          sx={styles.holidaysItemNameInput}
        />
        <FormControlLabel
          sx={styles.holidaysItemRepeat}
          control={
            <Checkbox
              size="small"
              checked={block.repeat}
              disabled={readOnly}
              onChange={e => handleUpdateRepeat(block.id, e.target.checked)}
            />
          }
          label="Repeats every year"
        />
      </Box>
    );
  };

  return (
    <>
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: styles.holidaysModalPaper }}>
      <Box sx={styles.holidaysModalHeader}>
        <Box sx={styles.holidaysModalHeaderIcon}>🗓</Box>
        <Box sx={styles.holidaysModalHeaderText}>
          <Typography sx={styles.holidaysModalTitle}>Holidays &amp; Days Off</Typography>
          <Typography sx={styles.holidaysModalSubtitle}>
            {isMultiSite ? 'Sites' : 'Site'}: {site || '—'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={styles.holidaysModalCloseButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.holidaysModalBody}>
        <Box sx={styles.holidaysCalWrap}>
          <Box sx={styles.holidaysCalNav}>
            <IconButton size="small" onClick={() => goToMonth(-1)} disabled={isLoading}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={styles.holidaysCalLabel}>
              {formatMonthLabel(calendarYear, calendarMonth)}
            </Typography>
            <IconButton size="small" onClick={() => goToMonth(1)} disabled={isLoading}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={styles.holidaysCalGrid}>
            {DAY_OF_WEEK_LABELS.map(label => (
              <Box key={label} sx={styles.holidaysCalDow}>
                {label}
              </Box>
            ))}
            {Array.from({ length: firstWeekday }, (_, i) => (
              <Box key={`pad-${i}`} sx={styles.holidaysCalCellPad} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              // Same grid, same cell count either way — only the cell's own content swaps
              // between a skeleton and the real date, so the calendar never resizes.
              if (isLoading) {
                return (
                  <Box key={day} sx={styles.holidaysCalCell}>
                    <Skeleton variant="circular" width={18} height={18} />
                  </Box>
                );
              }
              const key = isoDate(calendarYear, calendarMonth, day);
              const dow = new Date(calendarYear, calendarMonth, day).getDay();
              const block = blockAt(key);
              const blockIsSaved = block ? isSavedBlock(block) : false;
              const isPreview =
                previewRange && key >= previewRange[0] && key <= previewRange[1];
              const isToday = key === todayKey;
              return (
                <Box
                  key={key}
                  sx={{
                    ...styles.holidaysCalCell,
                    ...(dow === 0 || dow === 6 ? styles.holidaysCalCellWeekend : {}),
                    ...(block && blockIsSaved ? styles.holidaysCalCellHoliday : {}),
                    ...(block && !blockIsSaved ? styles.holidaysCalCellHolidayPending : {}),
                    ...(isPreview ? styles.holidaysCalCellPreview : {}),
                    ...(isToday ? styles.holidaysCalCellToday : {}),
                  }}
                  onMouseDown={() => handleStartDrag(key)}
                  onMouseEnter={() => onHoverDrag({ dateKey: key })}
                  title={block ? blockTitle(key) : undefined}>
                  {day}
                </Box>
              );
            })}
          </Box>

          <Box sx={styles.holidaysCalHint}>
            Tip: click a single date, or click-drag across several to set a multi-day block.
          </Box>
        </Box>

        <Box sx={styles.holidaysListPanel}>
          <Typography sx={styles.holidaysListTitle}>Selected days off</Typography>
          <Typography sx={styles.holidaysListHint}>
            Click a date to add it, click again to undo — already-saved days can only be
            removed via the trash icon.
          </Typography>

          <Box sx={styles.holidaysListItems}>
            {isLoading ? (
              [0, 1, 2].map(i => (
                <Skeleton key={i} variant="rounded" height={78} sx={{ borderRadius: '10px' }} />
              ))
            ) : (
              <>
                {sortedDraft.length === 0 && (
                  <Box sx={styles.holidaysListEmpty}>
                    No days off yet.
                    <br />
                    Click a date — or drag across several — to add them.
                  </Box>
                )}
                {!isMultiSite && sortedDraft.map(renderItem)}
                {isMultiSite && sortedDraft.length > 0 && (
                  <>
                    {unsavedDraft.length > 0 && (
                      <>
                        <Typography sx={styles.holidaysListSiteTitle}>New days off</Typography>
                        {unsavedDraft.map(renderItem)}
                      </>
                    )}
                    {sites.map(siteName => {
                      const siteBlocks = sortedDraft.filter(
                        b => isSavedBlock(b) && b.siteNames.includes(siteName)
                      );
                      return (
                        <React.Fragment key={siteName}>
                          <Typography sx={styles.holidaysListSiteTitle}>{siteName}</Typography>
                          {siteBlocks.length > 0 ? (
                            siteBlocks.map(renderItem)
                          ) : (
                            <Box sx={styles.holidaysListSiteEmpty}>No days off yet.</Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.holidaysModalFoot}>
        <Typography sx={styles.holidaysModalNote}>
          Recurring days off repeat on the same calendar date every year.
        </Typography>
        <Box sx={styles.footerActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={isSaving}
            sx={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={readOnly || isLoading || isSaving}
            sx={styles.saveButton}>
            {isSaving ? 'Saving…' : 'Save days off'}
          </Button>
        </Box>
      </Box>
    </Dialog>

    <Dialog open={!!pendingDeleteBlock} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
      <Box sx={styles.holidaysConfirmDeleteBody}>
        <Typography sx={styles.holidaysConfirmDeleteTitle}>Delete day off?</Typography>
        {pendingDeleteBlock && (
          <Typography sx={styles.holidaysConfirmDeleteText}>
            Are you sure you want to delete{' '}
            <b>
              {pendingDeleteBlock.name || 'this day off'} ·{' '}
              {formatDateRange(pendingDeleteBlock.start, pendingDeleteBlock.end)}
              {isMultiSite && ` · ${pendingDeleteBlock.siteNames.join(', ')}`}
            </b>
            ?
          </Typography>
        )}
        <Box sx={styles.footerActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancelDelete}
            disabled={isDeletingHoliday}
            sx={styles.cancelButton}>
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleConfirmDelete}
            disabled={isDeletingHoliday}
            sx={styles.saveButton}>
            {isDeletingHoliday ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </Box>
      </Box>
    </Dialog>
    </>
  );
};

export default HolidaysModal;
